import { useSelector } from 'react-redux';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import { useRouteProps } from 'src/common/providers/RoutePropsProvider';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { selectSearchResults } from 'src/common/selectors/search';
import { useStores } from 'src/common/stores';
import { selectIsUserSubscribed } from '../../selectors/me';
import { isProfile } from '../guideItemTypes';
import isContentPage from '../subscription/isContentPage';

export function getStoreGuideItem(guideId, state) {
  return isProfile(guideId) ? state.profiles[guideId] : state.categories[guideId];
}

export function useAdEligibilityConfig(guideIdOrGuideItem) {
  const { playerStore } = useStores();
  const playerStatus = playerStore.playerStatus();
  const metadata = playerStore.metadata() || {};
  const routeProps = useRouteProps();
  const isUserSubscribed = useSelector(selectIsUserSubscribed);
  const guideId = guideIdOrGuideItem || routeProps?.guideContext?.guideId;
  const storeGuideItem = useSelector((state) => getStoreGuideItem(guideId, state));
  const guideItem = typeof guideId === 'string' ? storeGuideItem : guideId;
  const isNowPlayingDialogOpen = useSelector(selectIsNowPlayingDialogOpen);
  const searchResults = useSelector(selectSearchResults);
  const isPageAdEligible = routeProps?.isSearchPage
    ? searchResults?.metadata?.properties?.ads?.adEligible
    : (isNowPlayingDialogOpen || isContentPage(routeProps)) &&
      (guideItem?.metadata?.properties?.ads?.adEligible ?? true);
  const isPlaying = playerStatus === playerStatuses.playing;
  const isStreamAdEligible = metadata?.ads?.canShowAds ?? true;

  if (isUserSubscribed) {
    return { isPageAdEligible: false, isNowPlayingAdEligible: false };
  }

  return {
    isPageAdEligible,
    isNowPlayingAdEligible: !isPlaying || isStreamAdEligible,
  };
}

/**
 * Determines whether ads are appropriate for the user/context. While Mint also
 * makes this determination, this function is primarily used to prevent ad-related
 * elements from rendering (i.e., containers), which Mint has no way to control.
 * Eventually, Mint's ad eligibility logic should be replaced in favor of this function.
 *
 * @param {string|object} [guideIdOrGuideItem]
 * @returns {function}
 */
export function useDetermineAdEligibility(guideIdOrGuideItem) {
  const { isPageAdEligible, isNowPlayingAdEligible } = useAdEligibilityConfig(guideIdOrGuideItem);

  return isPageAdEligible && isNowPlayingAdEligible;
}
